import { UIComponent } from './UIComponent.js';

/**
 * Hazard Report Component
 * Displays and manages safety hazard analysis results
 */
export class HazardReport extends UIComponent {
  constructor(element, options = {}) {
    super(element, options);
    this.hazards = null;
  }

  getDefaultOptions() {
    return {
      ...super.getDefaultOptions(),
      className: 'hazard-report',
      printable: false
    };
  }

  init() {
    super.init();
    this.setupLayout();
    this.renderReport();
    return this;
  }

  /**
   * Setup report layout
   */
  setupLayout() {
    this.element.innerHTML = `
      <div class="hazard-report-header">
        <div class="report-heading">
          <p class="report-eyebrow" style="color: #888888;">SUVA-konforme Analyse</p>
          <h2 class="report-title" style="color: #555555;">Safety Assessment Report</h2>
          <p class="report-meta" style="color: #666666;">Noch keine Analyse durchgeführt.</p>
        </div>
        <div class="report-actions">
          ${this.options.printable
        ? '<button class="btn btn-icon print-btn" title="Print Report">' +
        '<span class="material-icons">print</span></button>'
        : ''}
          <button class="btn btn-icon export-btn" title="Export Data">
            <span class="material-icons">download</span></button>
        </div>
      </div>
      
      ${this.renderRiskDistribution()}
      <div class="hazard-portfolio-wrapper">
        ${this.renderHazardPortfolio()}
      </div>

      <div class="hazard-report-content">
        <div class="hazards-list-container">
          <div class="hazards-list-header">
            <h3 style="color: #555555;">Identified Hazards</h3>
            <div class="list-controls">
              <select class="sort-select">
                <option value="priority">Sort by Priority</option>
                <option value="risk">Sort by Risk Level</option>
                <option value="category">Sort by Category</option>
                <option value="area">Sort by Area</option>
              </select>
            </div>
          </div>
          <div class="hazards-list">
            <!-- Hazards will be rendered here -->
          </div>
        </div>
      </div>
    `;

    this.setupEventListeners();
  }

  /**
   * Render risk overview counters
   */
  renderRiskOverview() {
    return `
      <div class="hazards-risk-overview grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" aria-live="polite">
        <div class="risk-pill risk-pill-high flex flex-col items-center justify-center p-3 rounded-xl border transition hover:shadow-md" style="background-color: rgba(200, 117, 102, 0.1); border-color: rgba(200, 117, 102, 0.2);">
          <span class="pill-value text-2xl font-bold" style="color: #C87566;" data-risk-count="high">0</span>
          <span class="pill-label text-[10px] font-bold uppercase tracking-wider mt-0.5" style="color: rgba(200, 117, 102, 0.6);">High Risk</span>
        </div>
        <div class="risk-pill risk-pill-medium flex flex-col items-center justify-center p-3 rounded-xl bg-amber-50 border border-amber-100 transition hover:shadow-md hover:border-amber-200">
          <span class="pill-value text-2xl font-bold text-amber-600" data-risk-count="medium">0</span>
          <span class="pill-label text-[10px] font-bold uppercase tracking-wider text-amber-400 mt-0.5">Medium Risk</span>
        </div>
        <div class="risk-pill risk-pill-low flex flex-col items-center justify-center p-3 rounded-xl bg-emerald-50 border border-emerald-100 transition hover:shadow-md hover:border-emerald-200">
          <span class="pill-value text-2xl font-bold text-emerald-600" data-risk-count="low">0</span>
          <span class="pill-label text-[10px] font-bold uppercase tracking-wider text-emerald-400 mt-0.5">Low Risk</span>
        </div>
        <div class="risk-pill risk-pill-total flex flex-col items-center justify-center p-3 rounded-xl bg-slate-50 border border-slate-100 transition hover:shadow-md hover:border-slate-200">
          <span class="pill-value text-2xl font-bold" style="color: #555555;" data-risk-count="total">0</span>
          <span class="pill-label text-[10px] font-bold uppercase tracking-wider mt-0.5" style="color: #888888;">Total</span>
        </div>
      </div>
    `;
  }

  renderRiskDistribution() {
    return `
      <section class="risk-distribution-card mb-4 p-4 rounded-xl border border-border/60 bg-white shadow-sm">
        <header class="risk-distribution-header flex justify-between items-end mb-3">
          <div>
            <h3 class="text-base font-bold" style="color: #555555;">Risk Distribution</h3>
            <p class="risk-distribution-subtitle text-xs mt-0.5" style="color: #888888;">Anteil der identifizierten Gefährdungen</p>
          </div>
          <span class="risk-distribution-total text-[10px] font-bold uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded-full" style="color: #888888;" data-risk-total>0 Gefährdungen</span>
        </header>
        <div class="risk-distribution-body space-y-3">
          <div class="risk-distribution-bar flex h-3 w-full overflow-hidden rounded-full bg-slate-100" data-risk-distribution>
            <span class="risk-segment high transition-all duration-500" style="background-color: #C87566;" data-risk-segment="high"></span>
            <span class="risk-segment medium bg-amber-500 transition-all duration-500" data-risk-segment="medium"></span>
            <span class="risk-segment low bg-emerald-500 transition-all duration-500" data-risk-segment="low"></span>
          </div>
          <div class="risk-distribution-empty text-center text-xs italic py-1" style="color: #888888;" data-risk-empty>Keine Daten verfügbar</div>
          <div class="risk-distribution-legend flex flex-wrap gap-3">
            <div class="legend-item high flex items-center gap-1.5 text-xs">
              <span class="w-2.5 h-2.5 rounded-full" style="background-color: #C87566;"></span>
              <span class="legend-label" style="color: #777777;">High Risk</span>
              <span class="legend-value font-bold" style="color: #555555;" data-risk-percent="high">0%</span>
            </div>
            <div class="legend-item medium flex items-center gap-1.5 text-xs">
              <span class="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
              <span class="legend-label" style="color: #777777;">Medium Risk</span>
              <span class="legend-value font-bold" style="color: #555555;" data-risk-percent="medium">0%</span>
            </div>
            <div class="legend-item low flex items-center gap-1.5 text-xs">
              <span class="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
              <span class="legend-label" style="color: #777777;">Low Risk</span>
              <span class="legend-value font-bold" style="color: #555555;" data-risk-percent="low">0%</span>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Filter controls
    // Sort control
    this.element.querySelector('.sort-select')?.addEventListener('change', (e) => {
      this.sortHazards(e.target.value);
    });

    // Action buttons
    this.element.querySelector('.export-btn')?.addEventListener('click', () => {
      this.handleExport();
    });

    this.element.querySelector('.print-btn')?.addEventListener('click', () => {
      this.handlePrint();
    });
  }

  /**
   * Set hazards data
   */
  setHazards(hazardsData) {
    console.log('📊 [HazardReport] setHazards called with:', hazardsData);
    this.hazards = hazardsData;
    this.renderReport();
  }

  /**
   * Render full report
   */
  renderReport() {
    console.log('🎨 [HazardReport] renderReport called');
    this.updateMeta();

    if (!this.hazards || !this.hazards.hazards) {
      console.warn('⚠️ [HazardReport] No hazards data to render');
      this.resetRiskInsights();
      this.element.querySelector('.hazards-list').innerHTML = `
        <div class="empty-state">
          <div class="empty-icon">🔍</div>
          <h3>No Hazards Found</h3>
          <p>Run a safety assessment to identify workplace hazards.</p>
        </div>
      `;
      return;
    }

    this.updateSummary();
    this.updateHazardPortfolio();
    this.renderHazardsList();
  }

  updateHazardPortfolio() {
    const container = this.element.querySelector('.hazard-portfolio-wrapper');
    if (container) {
      container.innerHTML = this.renderHazardPortfolio();
    }
  }

  /**
   * Update summary cards
   */
  updateSummary() {
    if (!this.hazards?.hazards) return;

    const stats = this.calculateStats();
    this.updateRiskCounters(stats);
    this.updateRiskDistribution(stats);
  }

  resetRiskInsights() {
    const emptyStats = { high: 0, medium: 0, low: 0, total: 0 };
    this.updateRiskCounters(emptyStats);
    this.updateRiskDistribution(emptyStats);
  }

  updateRiskCounters(stats) {
    if (!stats) return;
    const pills = this.element.querySelectorAll('[data-risk-count]');
    pills.forEach(pill => {
      const key = pill.dataset.riskCount;
      if (key && Object.prototype.hasOwnProperty.call(stats, key)) {
        pill.textContent = stats[key];
      }
    });

    const totalLabel = this.element.querySelector('[data-risk-total]');
    if (totalLabel) {
      const suffix = stats.total === 1 ? 'Gefährdung' : 'Gefährdungen';
      totalLabel.textContent = `${stats.total} ${suffix}`;
    }
  }

  updateRiskDistribution(stats) {
    const distribution = this.element.querySelector('[data-risk-distribution]');
    const emptyState = this.element.querySelector('[data-risk-empty]');
    const total = stats.total || 0;

    if (!distribution || !emptyState) return;

    if (total === 0) {
      emptyState.style.display = 'block';
      distribution.setAttribute('aria-hidden', 'true');
      distribution.classList.add('is-empty');
      distribution.style.opacity = '0.35';
    } else {
      emptyState.style.display = 'none';
      distribution.setAttribute('aria-hidden', 'false');
      distribution.classList.remove('is-empty');
      distribution.style.opacity = '1';
    }

    const segments = ['high', 'medium', 'low'];
    segments.forEach(key => {
      const segmentEl = this.element.querySelector(`[data-risk-segment="${key}"]`);
      const percentEl = this.element.querySelector(`[data-risk-percent="${key}"]`);
      const value = stats[key] || 0;
      const percent = total > 0 ? Math.round((value / total) * 100) : 0;

      if (segmentEl) {
        const grow = value > 0 ? value : 0.0001;
        segmentEl.style.flexGrow = grow;
        segmentEl.classList.toggle('is-empty', value === 0);
        segmentEl.setAttribute('aria-label', `${percent}% ${key} risk`);
      }

      if (percentEl) {
        percentEl.textContent = `${percent}%`;
      }
    });
  }

  /**
   * Render SUVA Hazard Portfolio (2x2 Matrix)
   */
  renderHazardPortfolio() {
    if (!this.hazards || !this.hazards.hazards || this.hazards.hazards.length === 0) {
      return '';
    }

    const hazards = this.hazards.hazards;
    const quadrants = {
      q1: [], // High Potential, Rules Not Available
      q2: [], // High Potential, Rules Available
      q3: [], // Low Potential, Rules Not Available
      q4: []  // Low Potential, Rules Available
    };

    hazards.forEach(h => {
      const isHigh = h.riskLevel === 'hoch';
      const rulesAvailable = h.rulesAvailable === true;

      if (isHigh && !rulesAvailable) quadrants.q1.push(h);
      else if (isHigh && rulesAvailable) quadrants.q2.push(h);
      else if (!isHigh && !rulesAvailable) quadrants.q3.push(h);
      else quadrants.q4.push(h);
    });

    const renderDots = (list) => {
      return list.map(h => {
        // Extract numeric ID for display (e.g., hazard_001 -> 1)
        const shortId = h.id.replace(/\D/g, '').replace(/^0+/, '') || h.id;
        return `<span class="portfolio-dot" title="${h.description} (${h.id})">${shortId}</span>`;
      }).join('');
    };

    return `
      <div class="hazard-portfolio-container mb-6 p-4 bg-slate-50 rounded-xl border border-border/60">
        <h3 class="text-sm font-bold mb-4" style="color: #555555;">Gefahren-Portfolio (SUVA)</h3>
        <div class="portfolio-grid">
          <!-- Y-Axis Label -->
          <div class="y-axis-label">Gefahrenpotenzial</div>
          
          <!-- Top Labels -->
          <div class="x-label-top-left">Regeln verfügbar</div>
          <div class="x-label-top-right">Regeln nicht/teilweise verfügbar</div>

          <!-- Quadrant 2: High Risk, Rules Available -->
          <div class="portfolio-quadrant q2">
            <div class="quadrant-label">2</div>
            <div class="quadrant-content">${renderDots(quadrants.q2)}</div>
          </div>

          <!-- Quadrant 1: High Risk, Rules Not Available -->
          <div class="portfolio-quadrant q1">
            <div class="quadrant-label">1</div>
            <div class="quadrant-content">${renderDots(quadrants.q1)}</div>
          </div>
          
          <!-- Y-Axis Labels (High/Low) -->
          <div class="y-label-high">hoch</div>
          <div class="y-label-low">tief</div>

          <!-- Quadrant 4: Low Risk, Rules Available -->
          <div class="portfolio-quadrant q4">
            <div class="quadrant-label">4</div>
            <div class="quadrant-content">${renderDots(quadrants.q4)}</div>
          </div>

          <!-- Quadrant 3: Low Risk, Rules Not Available -->
          <div class="portfolio-quadrant q3">
            <div class="quadrant-label">3</div>
            <div class="quadrant-content">${renderDots(quadrants.q3)}</div>
          </div>
        </div>
      </div>
    `;
  }

  updateMeta() {
    const metaElement = this.element.querySelector('.report-meta');
    if (!metaElement) return;

    if (!this.hazards || !Array.isArray(this.hazards.hazards) || this.hazards.hazards.length === 0) {
      metaElement.textContent = 'Noch keine Analyse durchgeführt.';
      return;
    }

    const meta = this.hazards.meta || {};
    const projectName = meta.projectName || 'Betriebsstruktur';
    const hazardCount = this.hazards.hazards.length;
    const formattedTimestamp = this.formatTimestamp(meta.generatedAt);
    const categoryCount = Array.isArray(meta.selectedCategories) ? meta.selectedCategories.length : null;
    const hazardsPerArea = meta.hazardsPerArea ? `${meta.hazardsPerArea} pro Bereich` : null;
    const totalAreas = typeof meta.totalAreas === 'number' ? `${meta.totalAreas} Bereiche` : null;

    const segments = [
      formattedTimestamp ? `Stand ${formattedTimestamp}` : null,
      `${hazardCount} Gefährdungen`,
      categoryCount ? `${categoryCount} Kategorien` : null,
      hazardsPerArea,
      totalAreas
    ].filter(Boolean);

    metaElement.textContent = [projectName, ...segments].filter(Boolean).join(' • ');
  }

  formatTimestamp(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    try {
      return new Intl.DateTimeFormat('de-CH', {
        dateStyle: 'short',
        timeStyle: 'short'
      }).format(date);
    } catch (error) {
      return date.toLocaleString();
    }
  }

  /**
   * Calculate hazard statistics
   */
  calculateStats() {
    const stats = { high: 0, medium: 0, low: 0, total: 0 };

    this.hazards.hazards.forEach(hazard => {
      stats.total++;
      switch (hazard.riskLevel) {
        case 'hoch':
          stats.high++;
          break;
        case 'mittel':
          stats.medium++;
          break;
        case 'gering':
          stats.low++;
          break;
      }
    });

    return stats;
  }

  /**
   * Render hazards list
   */
  renderHazardsList() {
    console.log('🎨 [HazardReport] renderHazardsList called');
    const listContainer = this.element.querySelector('.hazards-list');
    if (!listContainer) {
      console.error('❌ [HazardReport] .hazards-list container not found!');
      return;
    }

    const hazards = Array.isArray(this.hazards?.hazards) ? this.hazards.hazards : [];
    console.log(`📊 [HazardReport] Rendering ${hazards.length} hazards`);

    if (hazards.length === 0) {
      console.log('ℹ️ [HazardReport] No hazards to render, showing empty state');
      listContainer.innerHTML = `
        <div class="empty-state flex flex-col items-center justify-center py-12 text-center">
          <div class="empty-icon mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-3xl">🔍</div>
          <h3 class="text-lg font-bold text-slate-900">No Hazards Found</h3>
          <p class="text-slate-500 mt-1 max-w-md">Bitte führen Sie eine Sicherheitsanalyse durch, um Gefährdungen zu erhalten.</p>
        </div>
      `;
      return;
    }

    // Group hazards by area
    const hazardsByArea = hazards.reduce((acc, hazard) => {
      const area = hazard.area || 'Unbekannter Bereich';
      if (!acc[area]) acc[area] = [];
      acc[area].push(hazard);
      return acc;
    }, {});

    const renderAreaGroup = (area, areaHazards) => `
      <div class="area-group mb-8 rounded-xl border border-border/60 bg-white shadow-sm overflow-hidden">
        <div class="area-header flex items-center justify-between bg-slate-50 px-4 py-3 border-b border-border/60">
          <h3 class="text-base font-bold m-0" style="color: #555555;">${area}</h3>
          <button class="btn btn-sm btn-secondary add-risk-btn flex items-center gap-2" data-area="${area}">
            <span class="material-icons text-base">add</span>
            <span>Gefahr hinzufügen</span>
          </button>
        </div>
        <div class="hazard-table-wrapper">
          <table class="hazard-table w-full text-left text-sm">
            <thead class="bg-slate-50/50 border-b border-border/60">
              <tr>
                <th class="px-4 py-2 font-semibold whitespace-nowrap w-12" style="color: #666666;">ID</th>
                <th class="px-4 py-2 font-semibold whitespace-nowrap" style="color: #666666;">Kategorie</th>
                <th class="px-4 py-2 font-semibold whitespace-nowrap" style="color: #666666;">Risiko</th>
                <th class="px-4 py-2 font-semibold whitespace-nowrap" style="color: #666666;">Priorität</th>
                <th class="px-4 py-2 font-semibold min-w-[200px]" style="color: #666666;">Beschreibung</th>
                <th class="px-4 py-2 font-semibold min-w-[150px]" style="color: #666666;">Mögliche Folgen</th>
                <th class="px-4 py-2 font-semibold min-w-[200px]" style="color: #666666;">Massnahmen</th>
                <th class="px-4 py-2 font-semibold whitespace-nowrap" style="color: #666666;">Kosten</th>
                <th class="px-4 py-2 font-semibold text-right whitespace-nowrap" style="color: #666666;">Aktionen</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-border/60">
              ${areaHazards.map(hazard => this.renderHazardRow(hazard)).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;

    listContainer.innerHTML = Object.entries(hazardsByArea)
      .map(([area, areaHazards]) => renderAreaGroup(area, areaHazards))
      .join('');

    this.setupHazardItemEvents();
    this.setupAddRiskEvents();
  }

  setupAddRiskEvents() {
    this.element.querySelectorAll('.add-risk-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const button = e.currentTarget;
        const area = button.dataset.area;
        const originalContent = button.innerHTML;

        // Disable button and show spinner
        button.disabled = true;
        button.innerHTML = `
          <span class="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></span>
          <span>Wird generiert...</span>
        `;
        button.classList.add('opacity-75', 'cursor-not-allowed');

        try {
          // Get selected categories from the assessment config
          const selectedCategories = Array.from(document.querySelectorAll('.hazard-category-input:checked'))
            .map(input => input.value);

          // If only one category is selected, use it
          const category = selectedCategories.length === 1 ? selectedCategories[0] : null;

          this.emit('add-risk', { area, category });
        } catch (error) {
          // Reset button if something synchronous fails
          button.disabled = false;
          button.innerHTML = originalContent;
          button.classList.remove('opacity-75', 'cursor-not-allowed');
          console.error('Failed to dispatch add-risk event', error);
        }
      });
    });
  }

  /**
   * Render individual hazard item
   */
  renderHazardRow(hazard) {
    const riskClass = this.getRiskClass(hazard.riskLevel);
    const priorityClass = this.getPriorityClass(hazard.remediation?.priority);
    const stopLevel = hazard.remediation?.stopLevel || 'P';
    const measuresList = Array.isArray(hazard.remediation?.measures)
      ? hazard.remediation.measures
      : hazard.remediation?.measures ? [hazard.remediation.measures] : [];
    const guidelinesList = Array.isArray(hazard.suvaGuidelines)
      ? hazard.suvaGuidelines
      : hazard.suvaGuidelines ? [hazard.suvaGuidelines] : [];
    const measures = measuresList.join(', ');
    const guidelines = guidelinesList.join(', ');

    return `
      <tr data-hazard-id="${hazard.id}">
        <td>
          <div class="hazard-area-cell">
            <span class="hazard-area">${hazard.area}</span>
            <span class="hazard-id">${hazard.id}</span>
          </div>
        </td>
        <td>
          <div class="hazard-category">
            <span class="primary">${hazard.category}</span>
            <span class="secondary">${hazard.subcategory || ''}</span>
          </div>
        </td>
        <td>
          <span class="hazard-badge risk ${riskClass}">${hazard.riskLevel || 'n/a'}</span>
        </td>
        <td>
          <span class="hazard-badge priority ${priorityClass}">${hazard.remediation?.priority || 'n/a'}</span>
          <span class="hazard-badge stop">STOP ${stopLevel}</span>
        </td>
        <td>
          <div class="hazard-description">
            <p>${hazard.description}</p>
            ${guidelines ? `<small>SUVA: ${guidelines}</small>` : ''}
          </div>
        </td>
        <td>
          <div class="hazard-injuries text-sm text-slate-600">
            ${hazard.realisticInjuries || '<span class="text-slate-400 italic">Keine Angaben</span>'}
          </div>
        </td>
        <td>
          <div class="hazard-measures">
            ${measures || '<span class="placeholder">Keine Massnahmen definiert</span>'}
          </div>
        </td>
        <td>
          <div class="hazard-costs">
            <span>${hazard.remediation?.costEstimate || 'CHF 0-1000'}</span>
            <small>Fällig: ${this.calculateDueDate(hazard.remediation?.priority)}</small>
          </div>
        </td>
        <td class="hazard-actions">
          <button class="btn btn-sm btn-secondary edit-hazard" title="Anpassen">
            <span class="material-icons">edit</span>
          </button>
          <button class="btn btn-sm btn-danger delete-hazard" title="Löschen">
            <span class="material-icons">delete</span>
          </button>
        </td>
      </tr>
    `;
  }

  /**
   * Get CSS class for risk level
   */
  getRiskClass(riskLevel) {
    const classes = {
      hoch: 'danger',
      mittel: 'warning',
      gering: 'success'
    };
    return classes[riskLevel] || 'secondary';
  }

  /**
   * Get CSS class for priority
   */
  getPriorityClass(priority) {
    const classes = {
      sofort: 'danger',
      fristig: 'warning',
      mittel: 'info',
      langfristig: 'secondary'
    };
    return classes[priority] || 'secondary';
  }

  /**
   * Calculate due date from priority
   */
  calculateDueDate(priority) {
    const today = new Date();

    switch (priority) {
      case 'sofort':
        return today.toLocaleDateString();
      case 'fristig':
        return new Date(today.setDate(today.getDate() + 30)).toLocaleDateString();
      case 'mittel':
        return new Date(today.setDate(today.getDate() + 90)).toLocaleDateString();
      default:
        return new Date(today.setDate(today.getDate() + 180)).toLocaleDateString();
    }
  }

  /**
   * Sort hazards
   */
  sortHazards(sortBy) {
    if (!this.hazards?.hazards) return;

    const sortFunctions = {
      priority: (a, b) => {
        const priorityOrder = { sofort: 0, fristig: 1, mittel: 2, langfristig: 3 };
        const aPriority = priorityOrder[a.remediation?.priority] || 4;
        const bPriority = priorityOrder[b.remediation?.priority] || 4;
        return aPriority - bPriority;
      },

      risk: (a, b) => {
        const riskOrder = { hoch: 0, mittel: 1, gering: 2 };
        const aRisk = riskOrder[a.riskLevel] || 3;
        const bRisk = riskOrder[b.riskLevel] || 3;
        return aRisk - bRisk;
      },

      category: (a, b) => (a.category || '').localeCompare(b.category || ''),
      area: (a, b) => (a.area || '').localeCompare(b.area || '')
    };

    const sortFn = sortFunctions[sortBy] || sortFunctions.priority;
    this.hazards.hazards.sort(sortFn);

    this.renderHazardsList();
    this.emit('hazardsSorted', sortBy);
  }

  /**
   * Setup hazard item event handlers
   */
  setupHazardItemEvents() {
    this.element.querySelectorAll('.edit-hazard').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const row = e.target.closest('tr');
        if (!row) return;
        const hazardId = row.dataset.hazardId;
        this.handleEditHazard(hazardId);
      });
    });

    this.element.querySelectorAll('.delete-hazard').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const row = e.target.closest('tr');
        if (!row) return;
        const hazardId = row.dataset.hazardId;
        this.handleDeleteHazard(hazardId);
      });
    });
  }

  /**
   * Handle hazard edit
   */
  handleEditHazard(hazardId) {
    if (!hazardId) return;
    const hazard = this.hazards.hazards.find(h => h.id === hazardId);
    if (hazard) {
      this.emit('editHazard', hazard);
    }
  }

  /**
   * Handle hazard deletion
   */
  handleDeleteHazard(hazardId) {
    if (!hazardId) return;
    if (confirm('Möchten Sie diese Gefährdung wirklich löschen?')) {
      const index = this.hazards.hazards.findIndex(h => h.id === hazardId);
      if (index > -1) {
        this.hazards.hazards.splice(index, 1);
        this.renderReport();
        this.emit('hazardDeleted', hazardId);
      }
    }
  }

  /**
   * Handle export
   */
  handleExport() {
    this.emit('exportRequested', this.hazards);
  }

  /**
   * Handle print
   */
  handlePrint() {
    window.print();
    this.emit('printRequested');
  }

}
